import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useEffect, useState, memo } from 'react';
import { useInView } from 'react-intersection-observer';

type Dealer = {
  dealerCompanyId: number;
  dealerCompanyName: string;
  imageUrl: string;
  dealerPackageId: number;
  packageName: string;
  showDealerInVehicleList: boolean;
  showDealerCompanyLogo: boolean;
};

type Props = {
  dealerData: Dealer[];
  title?: string;
  adSpaceList?: any[];
};

const DealerCard = ({ dealer }: { dealer: Dealer }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={dealer.dealerCompanyId}>
      <Box
        ref={ref}
        component={RouterLink}
        to={`/dealer-vehicles/${dealer.dealerCompanyId}`}
        sx={{
          width: '100%',
          height: 80,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s ease-in-out',
          textDecoration: 'none',
          '&:hover': {
            transform: 'scale(1.03)',
            cursor: 'pointer',
            boxShadow: 1,
          },
        }}
      >
        {inView ? (
          <Box
            component="img"
            src={dealer.imageUrl}
            alt={dealer.dealerCompanyName}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              p: 1,
            }}
            loading="lazy"
          />
        ) : null}
      </Box>
    </Grid>
  );
};

// --- Helper Component for the Ad Logic ---
const DealerSideAd = ({ ads }: { ads: any[] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (ads.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % ads.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [ads]);

  // 🚀 UPDATED HEIGHT HERE
  // xs (Mobile): Increased to 300 (was 200) to match the other ad style
  // sm (Tablet): 300
  // md (Desktop): 280 (kept compact for sidebars)
  const heightSettings = { xs: 350, sm: 350, md: 300 };
  const widthSettings = '95%';

  if (ads.length === 0) {
    // Fallback if no ads exist
    return (
      <Box
        sx={{
          width: widthSettings,
          height: heightSettings,
          // ✅ REMOVED: borderRadius and border for floating look
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
          py: 3,
          mx: 'auto',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.secondary',
            fontWeight: 'bold',
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: 1,
            opacity: 0.7,
          }}
        >
          Sponsored
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Ad Space Available
        </Typography>
      </Box>
    );
  }

  const currentAd = ads[currentIndex];

  return (
    <Box
      sx={{
        width: widthSettings,
        height: heightSettings,
        // ✅ REMOVED: overflow, borderRadius, and boxShadow so it just floats
        position: 'relative',
        mx: 'auto',
      }}
    >
      <a
        href={currentAd.adSpaceThumbnailUrl || '#'}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <Box
          component="img"
          src={currentAd.adSpaceImageUrl}
          alt={currentAd.name || 'Dealer Advertisement'}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            // ✅ Optional: Add a subtle drop shadow directly to the image if desired,
            // instead of the container. Uncomment below to add it back.
            // filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.1))',
          }}
        />
      </a>
    </Box>
  );
};

const DealerListSection = memo(({ dealerData, title = 'Our Partner Dealers', adSpaceList = [] }: Props) => {
  const navigate = useNavigate();
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);

  const visibleDealers = dealerData
    .filter(dealer => dealer.showDealerCompanyLogo)
    .slice(0, 10);

  useEffect(() => {
    visibleDealers.forEach((dealer) => {
      const img = new Image();
      img.src = dealer.imageUrl;
    });
  }, [visibleDealers]);

  useEffect(() => {
    if (selectedDealer) {
      navigate(`/dealer-vehicles/${selectedDealer.dealerCompanyId}`);
    }
  }, [selectedDealer, navigate]);

  // Filter ads for homepage-dealer
  const dealerAds = adSpaceList.filter(ad => ad.adPositionCode === 'homepage-dealer');

  return (
    <Box sx={{ py: 5 }} maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Autocomplete
            options={dealerData}
            getOptionLabel={(option) => option.dealerCompanyName}
            onChange={(_, value) => setSelectedDealer(value)}
            renderInput={(params) => (
              <TextField {...params} label="Choose Dealer" size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  component="img"
                  src={option.imageUrl}
                  alt={option.dealerCompanyName}
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: 'contain',
                    mr: 1,
                  }}
                />
                {option.dealerCompanyName}
              </li>
            )}
            sx={{ minWidth: 240 }}
          />

          <Button size="small" onClick={() => navigate('/dealer-vehicles')}>
            View All
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* LEFT: Dealer List */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Grid container spacing={2}>
            {visibleDealers.map((dealer) => (
              <DealerCard key={dealer.dealerCompanyId} dealer={dealer} />
            ))}
          </Grid>
        </Grid>

        {/* RIGHT: Dealer Ads (Updated Component) */}
        <Grid size={{ xs: 12, md: 4 }}>
          <DealerSideAd ads={dealerAds} />
        </Grid>

      </Grid>
    </Box>
  );
});

export default DealerListSection;